import React from 'react';
import { motion } from 'framer-motion';

interface ApiCodeDisplayProps {
  apiCode?: {
    method: string;
    url: string;
    headers: Record<string, string>;
    body: Record<string, any>;
  };
}

export const ApiCodeDisplay: React.FC<ApiCodeDisplayProps> = ({ apiCode }) => {
  if (!apiCode) return null;

  const formatJsonWithHighlighting = (obj: any, indent = 0): JSX.Element => {
    if (typeof obj === 'string') {
      return <span className="text-green-600">"{obj}"</span>;
    }
    if (typeof obj === 'number') {
      return <span className="text-green-600">{obj.toString()}</span>;
    }
    if (typeof obj === 'boolean') {
      return <span className="text-green-600">{obj.toString()}</span>;
    }
    if (obj === null) {
      return <span className="text-green-600">null</span>;
    }
    
    if (Array.isArray(obj)) {
      return (
        <span>
          {'[\n'}
          {obj.map((item, index) => (
            <span key={index}>
              {'  '.repeat(indent + 1)}
              {formatJsonWithHighlighting(item, indent + 1)}
              {index < obj.length - 1 ? ',' : ''}
              {'\n'}
            </span>
          ))}
          {'  '.repeat(indent)}{']'}
        </span>
      );
    }
    
    if (typeof obj === 'object') {
      const entries = Object.entries(obj);
      return (
        <span>
          {'{\n'}
          {entries.map(([key, value], index) => (
            <span key={key}>
              {'  '.repeat(indent + 1)}
              <span className="text-blue-600">"{key}"</span>
              <span className="text-gray-600">: </span>
              {formatJsonWithHighlighting(value, indent + 1)}
              {index < entries.length - 1 ? ',' : ''}
              {'\n'}
            </span>
          ))}
          {'  '.repeat(indent)}{'}'}
        </span>
      );
    }
    
    return <span className="text-green-600">{String(obj)}</span>;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="code-block"
    >
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <span className="text-blue-600 font-semibold">curl</span>
          <span className="text-gray-600">--request</span>
          <span className="text-green-600 font-semibold">{apiCode.method}</span>
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-gray-600">--url</span>
          <span className="text-blue-600">{apiCode.url}</span>
        </div>
        
        {Object.entries(apiCode.headers).map(([key, value]) => (
          <div key={key} className="flex items-center space-x-2">
            <span className="text-gray-600">--header</span>
            <span className="text-green-600">'{key}: {value}'</span>
          </div>
        ))}
        
        <div className="flex items-center space-x-2">
          <span className="text-gray-600">--data</span>
        </div>
        
        <div className="ml-4 text-sm">
          <pre className="whitespace-pre-wrap">
            {formatJsonWithHighlighting(apiCode.body)}
          </pre>
        </div>
      </div>
    </motion.div>
  );
};
